// Detect chains of self-replies inside a unified feed (e.g. Dame replying
// to her own posts to thread an idea) and rearrange them into linear,
// oldest -> newest reading order. The thread floats up to the position of
// its newest post — so its position relative to the rest of the feed
// matches what a reverse-chronological reader expects, but inside the
// run, posts read top-down like the conversation was written.
//
// Items not in a thread (singletons, or replies whose parent isn't
// visible in the current feed window) are passed through unchanged.
//
// Each item in a thread is decorated with a `_thread` block that the
// renderer uses to suppress the redundant "replying to @dame.is" hint on
// continuation posts and to draw a connector between members:
//
//   {
//     length: total posts in this thread,
//     position: 0-based index, 0 = oldest
//     isFirst, isLast,
//     anchorAt: createdAt of the newest post (used for day-grouping),
//     continuesPrev: true if this post's parent is the post directly
//                    above it in the rendered thread,
//     rootAtUri: the atUri of the oldest post in the thread (for keys),
//     selfReplies: how many posts in the thread reply to this one, which
//                  its replyCount includes,
//   }

import { compareIsoDesc } from './time.js';

const POSTING_VERB = 'posting';

/** The at:// URI of the post of Dame's that `item` replies to, if any. */
export function selfReplyParentUri(item, myDid) {
  if (item?.verb !== POSTING_VERB) return null;
  const parentUri = item.payload?.reply?.parent?.uri;
  if (!parentUri) return null;
  const parentDid = item.payload?.parent?.author?.did;
  if (parentDid && parentDid !== myDid) return null;
  // No author hydrated — fall back to URI inspection. AT URIs are
  // `at://<did>/<collection>/<rkey>`.
  if (!parentDid && !parentUri.startsWith(`at://${myDid}/`)) return null;
  return parentUri;
}

export function groupSelfReplyThreads(items, myDid) {
  if (!Array.isArray(items) || items.length < 2 || !myDid) return items;

  // Fast index of feed-visible items by their atUri so we can walk
  // child -> parent edges without a quadratic scan.
  const indexByUri = new Map();
  items.forEach((item, i) => {
    if (item?.atUri) indexByUri.set(item.atUri, i);
  });

  // Union-find over feed indices. Each set is a thread.
  const parent = items.map((_, i) => i);
  function find(x) {
    while (parent[x] !== x) {
      parent[x] = parent[parent[x]];
      x = parent[x];
    }
    return x;
  }
  function union(a, b) {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent[ra] = rb;
  }

  // childIdx -> parentIdx (only when the parent is also in the feed), and
  // parentIdx -> how many of its children are.
  const directParent = new Map();
  const childCount = new Map();
  for (let i = 0; i < items.length; i++) {
    const parentUri = selfReplyParentUri(items[i], myDid);
    if (!parentUri) continue;
    const pIdx = indexByUri.get(parentUri);
    if (pIdx === undefined) continue;
    union(i, pIdx);
    directParent.set(i, pIdx);
    childCount.set(pIdx, (childCount.get(pIdx) || 0) + 1);
  }

  // Bucket items by root.
  const groups = new Map();
  for (let i = 0; i < items.length; i++) {
    const root = find(i);
    if (!groups.has(root)) groups.set(root, []);
    groups.get(root).push(i);
  }

  // Walk the feed in original (newest-first) order. The first time we
  // hit any member of a thread, emit the whole thread in chronological
  // (oldest-first) order anchored at the newest post's position.
  const consumed = new Set();
  const output = [];
  for (let i = 0; i < items.length; i++) {
    if (consumed.has(i)) continue;
    const root = find(i);
    const groupIndices = groups.get(root);
    if (groupIndices.length === 1) {
      output.push(items[i]);
      consumed.add(i);
      continue;
    }
    const groupSet = new Set(groupIndices);
    const sorted = [...groupIndices].sort(
      (a, b) => -compareIsoDesc(items[a].createdAt, items[b].createdAt),
    );
    const anchorAt = items[sorted[sorted.length - 1]].createdAt;
    const rootAtUri = items[sorted[0]].atUri || null;
    sorted.forEach((idx, pos) => {
      const parentIdx = directParent.get(idx);
      const continuesPrev =
        parentIdx !== undefined && groupSet.has(parentIdx);
      output.push({
        ...items[idx],
        _thread: {
          length: sorted.length,
          position: pos,
          isFirst: pos === 0,
          isLast: pos === sorted.length - 1,
          anchorAt,
          continuesPrev,
          rootAtUri,
          selfReplies: childCount.get(idx) || 0,
        },
      });
      consumed.add(idx);
    });
  }

  return output;
}

/**
 * Union-find over a feed's self-reply edges: `find(i)` names the thread item
 * `i` belongs to (an item in none names itself).
 */
function threadFinder(items, myDid) {
  const indexByUri = new Map();
  items.forEach((item, i) => {
    if (item?.atUri) indexByUri.set(item.atUri, i);
  });
  const parent = items.map((_, i) => i);
  function find(x) {
    while (parent[x] !== x) {
      parent[x] = parent[parent[x]];
      x = parent[x];
    }
    return x;
  }
  for (let i = 0; i < items.length; i++) {
    const pIdx = indexByUri.get(selfReplyParentUri(items[i], myDid));
    if (pIdx === undefined) continue;
    const a = find(i);
    const b = find(pIdx);
    if (a !== b) parent[a] = b;
  }
  return find;
}

/**
 * A self-thread is one entry, so a view of the feed shows all of it or none
 * of it: every item of `all` that shares a thread with something in `subset`
 * joins `subset`. That's what lets a filter or search that matches one post
 * bring the whole thread, and lets the feed's window, when a thread's newest
 * post is in view, bring its older posts however far down they sit.
 *
 * `subset` holds items of `all` (the same objects). The result keeps `all`'s
 * order; it's `subset` itself when nothing needed adding.
 */
export function completeSelfThreads(subset, all, myDid) {
  if (!Array.isArray(subset) || !Array.isArray(all) || !myDid || !subset.length) {
    return subset;
  }
  const find = threadFinder(all, myDid);
  const wanted = new Set(subset);
  const threads = new Set();
  all.forEach((item, i) => {
    if (wanted.has(item)) threads.add(find(i));
  });
  const out = [];
  let added = 0;
  all.forEach((item, i) => {
    if (wanted.has(item)) {
      out.push(item);
      wanted.delete(item);
    } else if (threads.has(find(i))) {
      out.push(item);
      added += 1;
    }
  });
  if (!added) return subset;
  // Anything of `subset` that `all` didn't hold keeps its place at the end.
  return wanted.size ? [...out, ...wanted] : out;
}

/**
 * The posts of Dame's that self-replies in `items` answer but that neither
 * `items` nor `known` holds: a thread's earlier posts that were never loaded
 * (older than the feed reaches). Sorted, so the list can key a fetch.
 */
export function missingSelfParents(items, known, myDid) {
  if (!myDid) return [];
  const have = new Set();
  for (const item of [...(known || []), ...(items || [])]) {
    if (item?.atUri) have.add(item.atUri);
  }
  const missing = new Set();
  for (const item of items || []) {
    const parentUri = selfReplyParentUri(item, myDid);
    if (parentUri && !have.has(parentUri)) missing.add(parentUri);
  }
  return [...missing].sort();
}

/**
 * The ledger's form of a self-thread: one row for the whole run. Takes the
 * output of `groupSelfReplyThreads` and folds each thread's members (which it
 * emits together, oldest first) into a single row that copies the oldest
 * post's fields, so the row reads, dates and links like the thread's start,
 * and carries every member, oldest first, as `_thread.posts`. Everything else
 * passes through untouched.
 */
export function collapseSelfThreads(items) {
  if (!Array.isArray(items)) return items;
  const out = [];
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const thread = item?._thread;
    const posts = thread?.isFirst ? items.slice(i, i + thread.length) : null;
    // Only a whole run folds: a member missing from the slice (it shouldn't
    // be) leaves the rows as they were rather than dropping one.
    const whole =
      posts &&
      posts.length === thread.length &&
      posts.every((p, j) => p?._thread?.position === j);
    if (!whole || posts.length < 2) {
      out.push(item);
      continue;
    }
    out.push({ ...item, _thread: { ...thread, posts } });
    i += posts.length - 1;
  }
  return out;
}

/** Whether a row is a folded self-thread (see `collapseSelfThreads`). */
export function isSelfThreadRow(item) {
  return Array.isArray(item?._thread?.posts) && item._thread.posts.length > 1;
}

/**
 * A folded thread's engagement, added up across its posts. Every post after
 * the first is a reply to another post in the thread, and the AppView counts
 * it in that parent's replies, so those come off: what's left is replies from
 * anyone else (and any of Dame's that branch off the thread).
 */
export function threadEngagement(posts) {
  const sum = (key) => posts.reduce((n, p) => n + (p?.payload?.[key] || 0), 0);
  return {
    likeCount: sum('likeCount'),
    replyCount: Math.max(0, sum('replyCount') - (posts.length - 1)),
    repostCount: sum('repostCount'),
    quoteCount: sum('quoteCount'),
  };
}

/**
 * Day-grouping key picker that pulls thread posts onto the newest
 * member's day, so a thread that crosses midnight stays together under
 * the most recent day header instead of splitting in two.
 */
export function threadAwareDateKey(item) {
  return item?._thread?.anchorAt || item?.createdAt || null;
}

/**
 * Set of atUris that participate in a visible self-reply thread of 2+
 * posts (i.e., at least one self-reply edge between them is in the
 * feed window). Used by the feed filter to keep continuing-thread
 * members in the timeline while standalone replies get hidden — the
 * filter runs before `groupSelfReplyThreads`, so it can't read the
 * `_thread` annotation directly.
 *
 * Thread groups whose root post is itself a reply to someone else are
 * EXCLUDED — even though the chain is technically self-replies after
 * that root, the whole conversation is in a reply-to-other context,
 * so it reads as replies (and gets hidden) when the user has chosen
 * to filter replies out.
 */
export function selfThreadMembers(items, myDid) {
  if (!Array.isArray(items) || items.length < 2 || !myDid) return new Set();

  const indexByUri = new Map();
  items.forEach((item, i) => {
    if (item?.atUri) indexByUri.set(item.atUri, i);
  });

  const parent = items.map((_, i) => i);
  function find(x) {
    while (parent[x] !== x) {
      parent[x] = parent[parent[x]];
      x = parent[x];
    }
    return x;
  }
  function union(a, b) {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent[ra] = rb;
  }

  for (let i = 0; i < items.length; i++) {
    const parentUri = selfReplyParentUri(items[i], myDid);
    if (!parentUri) continue;
    const pIdx = indexByUri.get(parentUri);
    if (pIdx === undefined) continue;
    union(i, pIdx);
  }

  const sizes = new Map();
  for (let i = 0; i < items.length; i++) {
    const r = find(i);
    sizes.set(r, (sizes.get(r) || 0) + 1);
  }

  // Mark groups whose root (= a member with no in-feed self-reply
  // parent) is itself a reply to someone other than me as "tainted".
  // Tainted groups don't get kept in the result, so their continuing
  // posts fall back to the reply bucket when filtering.
  const tainted = new Set();
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item?.verb !== 'posting') continue;
    const parentUri = item.payload?.reply?.parent?.uri;
    if (!parentUri) continue;
    const selfParentUri = selfReplyParentUri(item, myDid);
    const selfParentInFeed =
      selfParentUri && indexByUri.has(selfParentUri);
    if (selfParentInFeed) continue; // not the root of its chain
    const parentDid = item.payload?.parent?.author?.did;
    const isReplyToOther = parentDid
      ? parentDid !== myDid
      : !parentUri.startsWith(`at://${myDid}/`);
    if (isReplyToOther) tainted.add(find(i));
  }

  const out = new Set();
  for (let i = 0; i < items.length; i++) {
    const r = find(i);
    if (tainted.has(r)) continue;
    if ((sizes.get(r) || 0) > 1 && items[i]?.atUri) {
      out.add(items[i].atUri);
    }
  }
  return out;
}
